import { Modal } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { useEffect } from "react";
import { useContainerRef } from "../../../hooks/useContainerRef";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { Frequency } from "../../../utils/enums";
import { isDefined } from "../../../utils/isDefined";
import { decamelize } from "../../../utils/SnakeCamelConversion";
import type { Identity } from "../EdaTable/Mbk";
import type { CreateEdaVersionFormProps } from "./CreateEdaVersionForm/CreateEdaVersionForm";
import { CreateEdaVersionForm } from "./CreateEdaVersionForm/CreateEdaVersionForm";
import { EDA_FORM_FIELDS } from "./CreateEdaVersionModal.constants";
import type {
  ComputationPayload,
  EdaVersionFormValues,
  EstimationPayload,
  GenericPayload,
  MeterPayload
} from "./CreateEdaVersionModal.types";
import {
  ESTIMATION_PROCEDURES_CONSTANT_LOAD,
  ESTIMATION_PROCEDURES_EXAMPLE_MEASURING,
  ESTIMATION_PROCEDURES_MISC,
  ESTIMATION_PROCEDURES_NON_CALIBRATED_MEASURING,
  ESTIMATION_PROCEDURES_PARITAL_RATIO,
  ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES,
  ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES_15_MIN,
  ORIGIN_COMPUTATION,
  ORIGIN_ESTIMATION,
  ORIGIN_METER
} from "./CreateEdaVersionModal.types";
import { useCreateEdaVersion } from "./hooks/useCreateEdaVersion";
import { useIdentityList } from "./hooks/useIdentityList";
import { computeInitialFormStateFromPrefillData } from "./utils/computeInitialFormStateFromPrefillData";
import { createIdentity } from "./utils/createIdentity";

export interface CreateEdaVersionModalProps extends Pick<
  CreateEdaVersionFormProps,
  | "isNewVersion"
  | "prefillData"
  | "referencedIdentityOptions"
  | "siteId"
  | "tagOptions"
> {
  isOpen: boolean;
  onToggle: () => void;
  onVersionCreated: () => void;
}

export function CreateEdaVersionModal({
  isNewVersion,
  isOpen,
  prefillData,
  referencedIdentityOptions,
  siteId,
  tagOptions,
  onVersionCreated,
  onToggle
}: CreateEdaVersionModalProps) {
  const { containerNode, containerRef } = useContainerRef();
  const { showErrorToast } = useToast();
  const formInitialState = computeInitialFormStateFromPrefillData(
    prefillData,
    referencedIdentityOptions
  );

  const { data: identityList } = useIdentityList({
    shouldReturnDictionary: true
  });

  const {
    mutateAsync: mutateCreateEdaVersion,
    data,
    error,
    isPending
  } = useCreateEdaVersion();

  useEffect(() => {
    if (data && !isPending) {
      onVersionCreated();
      onToggle();
    }
  }, [data, isPending, onToggle, onVersionCreated]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error, showErrorToast]);

  async function handleSubmit(formValues: EdaVersionFormValues) {
    const components = [
      formValues[EDA_FORM_FIELDS.generator] ??
        prefillData?.originalVersion.identity.generator,

      formValues[EDA_FORM_FIELDS.consumer] ??
        prefillData?.originalVersion.identity.consumer
    ].filter((c): c is number => typeof c === "number");

    const firstDate = formValues[EDA_FORM_FIELDS.validity]?.[0];
    const lastDate = formValues[EDA_FORM_FIELDS.validity]?.[1];

    if (!isDefined(firstDate) || !isDefined(lastDate)) {
      return;
    }

    // date-range dates are in German format
    const firstDateLuxon = backendDateOrDateTimeToLuxonDateTime(firstDate);
    const lastDateLuxon = backendDateOrDateTimeToLuxonDateTime(lastDate);

    const identity: Identity = createIdentity(formValues, identityList, siteId);

    const genericPayload: GenericPayload = {
      ...prefillData?.originalVersion.versions[0],
      identity,
      tags:
        formValues[EDA_FORM_FIELDS.stromsteuerpflichtig] &&
        formValues[EDA_FORM_FIELDS.tags]
          ? formValues[EDA_FORM_FIELDS.tags]
          : [],
      customLabel: formValues[EDA_FORM_FIELDS.label] || null,
      components,
      siteId,
      firstDate: luxonDateTimeToBackendDateOrDateTime(
        firstDateLuxon,
        "ISO 8601"
      ),
      lastDate: luxonDateTimeToBackendDateOrDateTime(lastDateLuxon, "ISO 8601"),
      connections: formValues[EDA_FORM_FIELDS.connection]
        ? [formValues[EDA_FORM_FIELDS.connection]]
        : [],
      gasConnections: []
    };

    switch (formValues[EDA_FORM_FIELDS.origin]) {
      case ORIGIN_COMPUTATION: {
        if (
          !formValues[EDA_FORM_FIELDS.formula] ||
          !formValues[EDA_FORM_FIELDS.medium] ||
          !formValues[EDA_FORM_FIELDS.frequency]
        ) {
          return;
        }

        const computationPayload: ComputationPayload = {
          origin: formValues[EDA_FORM_FIELDS.origin],
          meter: null,
          estimation: null,
          computation: {
            formulaStr: formValues[EDA_FORM_FIELDS.formula],
            medium: formValues[EDA_FORM_FIELDS.medium],
            frequency: formValues[EDA_FORM_FIELDS.frequency]
          }
        };
        await mutateCreateEdaVersion({
          ...genericPayload,
          ...computationPayload
        });
        break;
      }
      case ORIGIN_ESTIMATION: {
        if (!formValues[EDA_FORM_FIELDS.estimationProcedure]) {
          return;
        }

        let _frequencyValue: Frequency;
        switch (formValues[EDA_FORM_FIELDS.estimationProcedure]) {
          case ESTIMATION_PROCEDURES_CONSTANT_LOAD:
          case ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES_15_MIN:
            _frequencyValue = Frequency.QuarterHour;
            break;
          case ESTIMATION_PROCEDURES_PARITAL_RATIO:
          case ESTIMATION_PROCEDURES_EXAMPLE_MEASURING:
          case ESTIMATION_PROCEDURES_NON_CALIBRATED_MEASURING:
            _frequencyValue = Frequency.Year;
            break;
          case ESTIMATION_PROCEDURES_TYPICAL_STANDARD_VALUES:
            _frequencyValue = Frequency.Year;
            break;
          case ESTIMATION_PROCEDURES_MISC:
          default:
            _frequencyValue = formValues[EDA_FORM_FIELDS.frequency]
              ? (formValues[EDA_FORM_FIELDS.frequency] as Frequency)
              : Frequency.QuarterHour;
            break;
        }

        if (!formValues[EDA_FORM_FIELDS.medium]) {
          return;
        }

        const estimationPayload: EstimationPayload = {
          origin: "estimation",
          meter: null,
          computation: null,
          estimation: {
            frequency: _frequencyValue ? _frequencyValue : "",
            procedure: decamelize(
              formValues[EDA_FORM_FIELDS.estimationProcedure]
            ),
            ratioOfEnergyData: formValues[EDA_FORM_FIELDS.ratioOfEnergyData],
            securityBuffer: formValues[
              EDA_FORM_FIELDS.securityBuffer
            ] as number,

            medium: formValues[EDA_FORM_FIELDS.medium],
            annualConsumption: formValues[EDA_FORM_FIELDS.annualConsumption],
            computation:
              formValues[EDA_FORM_FIELDS.formula] &&
              formValues[EDA_FORM_FIELDS.medium] &&
              _frequencyValue
                ? {
                    formulaStr: formValues[EDA_FORM_FIELDS.formula],
                    medium: formValues[EDA_FORM_FIELDS.medium],
                    frequency: _frequencyValue
                  }
                : undefined,
            constantLoadKw: formValues[EDA_FORM_FIELDS.constantLoadKw],
            numberOfConsumers: formValues[EDA_FORM_FIELDS.numberOfConsumers],
            referencedEdaIdentity: formValues[
              EDA_FORM_FIELDS.referencedEdaIdentity
            ]
              ? referencedIdentityOptions[
                  formValues[EDA_FORM_FIELDS.referencedEdaIdentity]
                ]
              : null
          }
        };
        await mutateCreateEdaVersion({
          ...genericPayload,
          ...estimationPayload
        });
        break;
      }
      case ORIGIN_METER: {
        if (
          !formValues[EDA_FORM_FIELDS.meter] ||
          !formValues[EDA_FORM_FIELDS.medium]
        ) {
          return;
        }

        const meterPayload: MeterPayload = {
          meter: {
            id: Number(formValues[EDA_FORM_FIELDS.meter]),
            medium: formValues[EDA_FORM_FIELDS.medium]
          },
          computation: null,
          estimation: null,
          origin: ORIGIN_METER
        };
        await mutateCreateEdaVersion({ ...genericPayload, ...meterPayload });
        break;
      }
      default:
        Sentry.captureMessage(
          `unknown origin: ${formValues[EDA_FORM_FIELDS.origin]}`
        );
        break;
    }
  }

  return (
    <Modal
      opened={isOpen}
      size="xl"
      title="Energiedatenerfassung hinzufügen"
      onClose={onToggle}
    >
      {identityList && (
        <CreateEdaVersionForm
          buttonContainer={containerNode}
          defaultValues={formInitialState}
          identityList={identityList}
          isNewVersion={isNewVersion}
          prefillData={prefillData}
          referencedIdentityOptions={referencedIdentityOptions}
          siteId={siteId}
          tagOptions={tagOptions}
          onSubmit={handleSubmit}
          onToggle={onToggle}
        />
      )}
      <ModalFooter ref={containerRef} />
    </Modal>
  );
}
